/***************************************************************************************
 * FileName      : MonitorConfigService.ts
 * Description   : 모니터 배치 설정 서비스
 *                 - assets/json/matterport/*.json에서 각 장비별 모니터 배치 정보 로드
 *                 - MonitorConfig 인터페이스 정의 및 re-export
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.11
 * Reference     : packages/scjeil-smart_ict_lab-app/assets/json/matterport/*.json, src/services/EquipmentDataService.ts
 ***************************************************************************************/

import { QuadCorners } from '../types/quad-image';
// 2026.02.11 : 박은정 : Matterport JSON에서 모니터 배치 정보 통합 로드 (monitor-config.json 제거)
import { EquipmentDataService } from './EquipmentDataService';

// ============================================================================
// 2026.02.11 : 박은정 : 모니터 설정 인터페이스
// ============================================================================

/** 2026.02.11 : 박은정 : 개별 모니터 설정 (ID, 이름, 장비 매핑, 색상, 이미지 경로, 4개 꼭짓점 좌표) */
export interface MonitorConfig {
  /** 모니터 고유 ID (예: 'monitor-01') */
  id: string;
  /** 모니터 표시명 (예: '모니터 1 (좌측)') */
  name: string;
  /** 매핑된 장비 ID (예: 'heat-exchanger') */
  equipmentId: string;
  /** UI 강조 색상 (예: '#6366f1') */
  color: string;
  /** 기본 장비 이미지 경로 (예: './assets/image/heat-exchanger.png') */
  defaultImageUrl: string;
  /** 4개 꼭짓점 좌표 (Matterport Anchor Position) */
  corners: QuadCorners;
}

// ============================================================================
// MonitorConfigService 클래스
// ============================================================================

/**
 * 2026.02.11 : 박은정 : 모니터 배치 설정 데이터 서비스 (싱글톤)
 * - assets/json/matterport/*.json에서 로드한 모니터 설정 제공
 * - 모니터 ID, 장비 ID로 조회 가능
 */
export class MonitorConfigService {
  private static instance: MonitorConfigService;
  // 2026.02.11 : 박은정 : Matterport JSON에서 추출한 모니터 설정 캐시
  private monitors: MonitorConfig[] = [];
  private modelId: string = '';

  private constructor() {
    this.loadFromMatterportData();
  }

  // 2026.02.11 : 박은정 : 싱글톤 패턴
  static getInstance(): MonitorConfigService {
    if (!MonitorConfigService.instance) {
      MonitorConfigService.instance = new MonitorConfigService();
    }
    return MonitorConfigService.instance;
  }

  // 2026.02.11 : 박은정 : Matterport JSON 데이터에서 모니터 설정 추출
  private loadFromMatterportData(): void {
    const equipmentDataService = EquipmentDataService.getInstance();
    const equipmentIds = equipmentDataService.getAllEquipmentIds();

    for (const equipmentId of equipmentIds) {
      const mpData = equipmentDataService.getMatterportData(equipmentId);
      if (!mpData || !mpData.monitor) continue;

      // 2026.02.11 : 박은정 : modelId는 모든 장비에서 동일 (첫 번째 값 사용)
      if (!this.modelId && mpData.modelId) {
        this.modelId = mpData.modelId;
      }

      const monitor = mpData.monitor;
      this.monitors.push({
        id: monitor.monitorId,
        name: monitor.name,
        equipmentId: equipmentId,
        color: monitor.color,
        defaultImageUrl: monitor.defaultImageUrl,
        corners: monitor.corners as unknown as QuadCorners,
      });
    }
  }

  // 2026.02.11 : 박은정 : Matterport 모델 ID 조회
  getModelId(): string {
    return this.modelId;
  }

  // 2026.02.11 : 박은정 : 전체 모니터 설정 배열 조회
  getAllMonitors(): MonitorConfig[] {
    return this.monitors;
  }

  // 2026.02.11 : 박은정 : 모니터 ID로 설정 조회
  getMonitorById(monitorId: string): MonitorConfig | null {
    return this.monitors.find((m) => m.id === monitorId) || null;
  }

  // 2026.02.11 : 박은정 : 장비 ID로 모니터 설정 조회
  getMonitorByEquipmentId(equipmentId: string): MonitorConfig | null {
    return this.monitors.find((m) => m.equipmentId === equipmentId) || null;
  }

  // 2026.02.11 : 박은정 : 모니터 수 조회
  getMonitorCount(): number {
    return this.monitors.length;
  }
}
